/**
 * University — the course catalog, grouped by track, with the learner's
 * progress and a search that jumps straight to any attribute's lesson.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, GraduationCap } from 'lucide-react';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div
      className="h-1.5 rounded-full bg-foreground/10 overflow-hidden"
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function CourseCard({ course }) {
  const { progress } = course;
  // The quiz counts as a step, so a course isn't full until it is passed.
  const steps = course.lesson_count + 1;
  const done = progress.lessons_done + (progress.quiz.passed ? 1 : 0);

  return (
    <Link
      to={`/university/${course.slug}`}
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4 hover:border-primary/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold leading-snug">{course.title}</h3>
        {progress.completed && <CheckCircle2 className="h-4 w-4 text-win shrink-0" aria-label="Completed" />}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed flex-1">{course.summary}</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {course.role && <Badge variant="outline">{course.role}</Badge>}
        <span>{course.lesson_count} lessons</span>
        {course.attributes.length > 0 && <span>{course.attributes.length} attributes</span>}
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{course.minutes} min</span>
      </div>
      <ProgressBar value={done} max={steps} />
    </Link>
  );
}

function CatalogSkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-40 rounded-lg" />)}
    </div>
  );
}

function AttributeSearch({ tracks }) {
  const [search, setSearch] = useState('');
  const index = useMemo(() => tracks.flatMap((track) => track.courses.flatMap((course) => (
    course.attributes.map((attr) => ({ ...attr, course: course.slug, courseTitle: course.title }))
  ))), [tracks]);

  const q = search.trim().toLowerCase();
  const matches = q ? index.filter((a) => a.alias.toLowerCase().includes(q)).slice(0, 12) : [];

  return (
    <div className="mb-6">
      <Input
        placeholder="Find an attribute: RSI, ROE, Donchian…"
        aria-label="Find an attribute"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="text-sm max-w-md"
      />
      {q && (
        <div className="mt-2 flex flex-col gap-1 max-w-md">
          {matches.length === 0 && <p className="text-xs text-muted-foreground">No attribute has that name.</p>}
          {matches.map((a) => (
            <Link
              key={a.alias}
              to={`/university/${a.course}/${a.lesson}#attr-${a.alias}`}
              className="flex items-baseline justify-between gap-3 rounded px-2 py-1.5 text-xs hover:bg-muted"
            >
              <span className="font-mono">{a.alias}</span>
              <span className="text-muted-foreground truncate">{a.courseTitle}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function University() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/university/courses')
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  const next = data?.next;

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto p-4 md:p-6">
        <div className="flex items-center gap-2 mb-1">
          <GraduationCap className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold">Jigri University</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Learn what each screener attribute measures and what it can do for a
          screener. These courses are educational and aren&apos;t investment advice.
        </p>

        {error && <p className="text-sm text-destructive">Couldn&apos;t load the courses. Try reloading the page.</p>}
        {!data && !error && <CatalogSkeleton />}

        {data && (
          <>
            <div className="rounded-lg border border-border bg-card p-4 mb-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground mb-0.5">
                    {data.courses_completed} of {data.courses_total} courses completed
                  </div>
                  <div className="text-sm font-medium truncate">
                    {next ? `Up next: ${next.course_title} — ${next.title}` : 'You’ve completed every course.'}
                  </div>
                </div>
                {next && (
                  <Button asChild size="sm">
                    <Link to={next.lesson ? `/university/${next.course}/${next.lesson}` : `/university/${next.course}/quiz`}>
                      Continue
                    </Link>
                  </Button>
                )}
              </div>
              <div className="mt-3"><ProgressBar value={data.courses_completed} max={data.courses_total} /></div>
            </div>

            <AttributeSearch tracks={data.tracks} />

            {data.tracks.map((track) => (
              <section key={track.slug} className="mb-8">
                <h2 className="text-base font-semibold">{track.title}</h2>
                <p className="text-xs text-muted-foreground mb-3">{track.description}</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {track.courses.map((course) => <CourseCard key={course.slug} course={course} />)}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </AppLayout>
  );
}
